<template>
  <v-dialog max-width="600px" v-model="dialog">
    <template v-slot:activator="{ props: activatorProps }">
      <!-- Custom activator slot - if provided, use it instead of default FAB -->
      <slot name="activator" :props="mergedActivatorProps(activatorProps)">
        <!-- Default FAB activator -->
        <v-fab v-bind="activatorProps" @click="handleActivatorClick($event, activatorProps.onClick)" color="error"
          icon="mdi-comment-alert" location="bottom left" size="large" :app="true" aria-label="Send feedback" />
      </slot>
    </template>
    <v-card class="pa-2">
      <v-card-title> Report Bug | Give Feedback</v-card-title>
      <v-card-subtitle class="text-wrap">
        <p>Here you can report a bug, or even give us a suggestion or idea!</p>
        <p class="mt-2">
          Please tell us the steps that you took to cause your issue so that we can reproduce and fix it
        </p>
      </v-card-subtitle>
      <v-card-text>
        <v-form ref="feedback_form">
          <v-row>
            <v-col cols="12">
              <v-text-field label="Title" v-model="title" :rules="titleRules" ref="focus" hide-details
                density="compact"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-textarea label="Details" v-model="details" filled hide-details density="compact"></v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              Personal information to include:
              <v-switch v-model="include_name" label="Name" hide-details density="compact" class="ml-5"></v-switch>
              <v-switch v-model="include_email" label="Email" hide-details density="compact" class="ml-5"></v-switch>
              <v-card-subtitle class="text-wrap">
                Providing email allows us to reach out for more information (if needed) and provide details on when the
                issue is resolved.
              </v-card-subtitle>

            </v-col>
          </v-row>
          <v-row>

            <v-btn @click="submitIssue" block color="primary" :loading="saving" :disabled="saving">Submit Report</v-btn>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/fb'
import { useAppState } from "@/composables/useAppState"
import { useFormDialog, type VFormRef } from "@/composables/useFormDialog"
import { useUserStore } from '@/stores/userStore'
import { rules } from "@/utils/validation"

const { showSnackbar } = useAppState()
const { dialog, handleActivatorClick, validateForm, resetForm, closeDialog } = useFormDialog()
const userStore = useUserStore()
const route = useRoute()

// Reactive state
const feedback_form = ref<VFormRef>()
const saving = ref(false)
const title = ref('')
const details = ref('')
const include_name = ref(false)
const include_email = ref(false)

// Validation rules
const titleRules = [rules.required()]

// Computed properties
const user = computed(() => userStore.user)

// Helper to merge activator props with click handler
const mergedActivatorProps = (activatorProps: Record<string, any>) => ({
  ...activatorProps,
  onClick: (e: Event) => handleActivatorClick(e, activatorProps.onClick)
})

const submitIssue = async () => {
  if (await validateForm(feedback_form.value)) {
    saving.value = true

    // Build the body with optional personal info
    let body = ""

    // User-provided details first
    body = "## Description\n\n"
    body += details.value || "(No details provided)"

    // Contact info section (if provided)
    if ((include_name.value && user.value?.displayName) || (include_email.value && user.value?.email)) {
      body += "\n\n## Contact Info\n\n"
      if (include_name.value && user.value?.displayName) {
        body += `- **Name:** ${user.value.displayName}\n`
      }
      if (include_email.value && user.value?.email) {
        body += `- **Email:** ${user.value.email}\n`
      }
    }

    // Environment info
    body += "\n\n## Environment\n\n"
    body += `- **User Agent:** ${navigator.userAgent}\n`
    body += `- **Platform:** ${navigator.platform}\n`
    body += `- **Language:** ${navigator.language}\n`
    body += `- **Screen:** ${window.screen.width}x${window.screen.height}\n`
    body += `- **Viewport:** ${window.innerWidth}x${window.innerHeight}\n`
    body += `- **Online:** ${navigator.onLine}\n`
    body += `- **Timestamp:** ${new Date().toISOString()}\n`

    // Auth status (without exposing sensitive data)
    body += "\n\n## User Context\n\n"
    body += `- **Logged In:** ${!!user.value}\n`
    if (user.value) {
      body += `- **Auth Provider:** ${user.value.providerData?.[0]?.providerId || 'unknown'}\n`
    }

    // Current route info
    body += "\n\n## Route Info\n\n"
    body += `- **Path:** ${route.path}\n`
    body += `- **Name:** ${String(route.name || 'unnamed')}\n`
    if (Object.keys(route.params).length > 0) {
      body += `- **Params:** \`${JSON.stringify(route.params)}\`\n`
    }
    if (Object.keys(route.query).length > 0) {
      body += `- **Query:** \`${JSON.stringify(route.query)}\`\n`
    }

    try {
      const submitFeedback = httpsCallable<
        { title: string; body: string },
        { success: boolean; issueNumber: number; issueUrl: string }
      >(functions, 'submitFeedback')

      await submitFeedback({
        title: title.value,
        body: body
      })

      saving.value = false
      closeDialog()
      resetForm(feedback_form.value)
      showSnackbar("Success 😄 Thanks for the feedback!", 10000)
    } catch (error: any) {
      console.error(
        `Failed to submit issue: "${error.name}: ${error.message}". We have been notified of the problem and are looking into it.`
      )
      saving.value = false
      showSnackbar(
        "Failed to Submit 😢 Something went wrong. We should be aware of this and are working to fix this system.",
        10000
      )
    }
  }
}
</script>
